import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import { Flame, Sparkles, Gift } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ProductCard from '../components/ProductCard';
import { productsAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';

// Category definitions with UK Handmade SEO metadata
const CATEGORIES = [
  {
    id: 'candles',
    name: 'Homemade Scented Candles',
    description: 'Hand-poured soya wax candles crafted with love',
    seoDescription: 'Shop handmade scented soya wax candles UK. Elephant candles, teddy bear candles, flower candles, angel wing candles. Eco-friendly, natural fragrances. Perfect handmade gifts for birthdays, weddings, Christmas.',
    icon: Flame,
    slug: 'homemade-scented-candles',
    keywords: 'handmade scented candles UK, soya wax candles, handmade candle gifts UK, artisan candles Buckinghamshire, eco-friendly candles UK, elephant candle handmade, teddy bear candle UK, flower candle gift, angel wing candle memorial'
  },
  {
    id: 'resin',
    name: 'Homemade Resin Designs',
    description: 'Unique resin art pieces and decorations',
    seoDescription: 'Discover unique handmade resin art UK. Artisan resin clocks, decorative pieces, home decor. One-of-a-kind handcrafted designs. Perfect handmade gifts and home decorations.',
    icon: Sparkles,
    slug: 'homemade-resin-designs',
    keywords: 'handmade resin crafts UK, resin art UK, handmade resin clock, artisan resin designs, unique handmade home decor UK, resin decorations Buckinghamshire, custom resin art'
  },
  {
    id: 'gifts',
    name: 'Perfect Gift',
    description: 'Curated gift sets for every occasion',
    seoDescription: 'Find perfect handmade gift sets UK. Beautifully curated artisan candle and resin gift boxes. Ideal for birthdays, weddings, baby showers, Christmas, Mother\'s Day.',
    icon: Gift,
    slug: 'perfect-gift',
    keywords: 'handmade gift sets UK, personalised gifts UK, handmade birthday gifts, handmade wedding gifts UK, handmade Christmas gifts, baby shower gifts handmade, Mother\'s Day handmade gifts, unique presents UK'
  }
];

// Skeleton loader component for products
const ProductSkeleton = memo(() => (
  <div className="bg-gray-800/50 rounded-lg overflow-hidden animate-pulse">
    <div className="aspect-square bg-gray-700/50" />
    <div className="p-4 space-y-3">
      <div className="h-5 bg-gray-700/50 rounded w-3/4" />
      <div className="h-4 bg-gray-700/50 rounded w-1/2" />
      <div className="h-8 bg-gray-700/50 rounded w-1/3" />
      <div className="h-10 bg-gray-700/50 rounded" />
    </div>
  </div>
));
ProductSkeleton.displayName = 'ProductSkeleton';

const Shop = ({ cartCount, onAddToCart }) => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [loadError, setLoadError] = useState(false);

  // Load products from backend API with retry logic
  const loadProducts = useCallback(async (retryCount = 0) => {
    const maxRetries = 3;
    setLoadError(false);
    
    try {
      const data = await productsAPI.getAll();
      setProducts(data);
      setLoadError(false);
      
      // Preload first image of each category for faster LCP
      if (data.length > 0) {
        const firstImages = data.slice(0, 3).map(p => {
          const img = p.scentsWithImages?.[0]?.images?.[0] || p.image;
          return img;
        }).filter(Boolean);
        
        firstImages.forEach(src => {
          if (src) {
            const link = document.createElement('link');
            link.rel = 'preload';
            link.as = 'image';
            link.href = src.includes('cloudinary') 
              ? src.replace('/upload/', '/upload/w_400,h_400,c_fill,q_auto,f_auto/')
              : src;
            document.head.appendChild(link);
          }
        });
      }
    } catch (error) {
      console.error('Error loading products:', error);
      
      // Retry if we haven't exceeded max retries
      if (retryCount < maxRetries) {
        console.log(`Retrying... attempt ${retryCount + 1} of ${maxRetries}`);
        setTimeout(() => loadProducts(retryCount + 1), 1000 * (retryCount + 1));
        return;
      }
      
      setLoadError(true);
      toast({
        title: 'Error',
        description: 'Failed to load products. Click "Retry" to try again.',
        variant: 'destructive',
      });
    } finally {
      if (retryCount === 0 || retryCount >= 2) {
        setIsLoading(false);
      }
    }
  }, [toast]);

  // Initial load
  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  // Memoized add to cart handler
  const handleAddToCart = useCallback((product) => {
    onAddToCart(product);
    toast({
      title: 'Added to cart!',
      description: `${product.name} has been added to your cart.`,
    });
  }, [onAddToCart, toast]);

  // Memoized function to get products by category
  const getProductsByCategory = useCallback((categoryId) => {
    return products.filter(p => {
      const productCategory = (p.category || '').toLowerCase();
      if (categoryId === 'candles') {
        return productCategory.includes('candle') || productCategory === 'candles' || !p.category || p.category === 'Candles';
      }
      if (categoryId === 'resin') {
        return productCategory.includes('resin');
      }
      if (categoryId === 'gifts') {
        return productCategory.includes('gift');
      }
      return false;
    });
  }, [products]);

  // Pre-compute category counts for quick links
  const categoryCounts = useMemo(() => {
    return CATEGORIES.reduce((acc, cat) => {
      acc[cat.id] = getProductsByCategory(cat.id).length;
      return acc;
    }, {});
  }, [getProductsByCategory]);

  // Generate structured data for SEO
  const generateStructuredData = useMemo(() => {
    const baseUrl = 'https://www.adinascrafts.co.uk';
    
    // Main ItemList for the shop
    const itemListSchema = {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      'name': 'Adina\'s Crafts Collection',
      'description': 'Browse our complete collection of handmade scented candles, resin designs, and gift sets.',
      'url': `${baseUrl}/shop`,
      'numberOfItems': products.length,
      'itemListElement': products.slice(0, 20).map((product, index) => ({
        '@type': 'ListItem',
        'position': index + 1,
        'item': {
          '@type': 'Product',
          '@id': `${baseUrl}/product/${product.id}`,
          'name': product.name,
          'description': product.description,
          'image': product.scentsWithImages?.[0]?.images?.[0] || product.image,
          'url': `${baseUrl}/product/${product.id}`,
          'offers': {
            '@type': 'Offer',
            'price': product.price,
            'priceCurrency': 'GBP',
            'availability': product.inStock !== false ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock'
          },
          'brand': {
            '@type': 'Brand',
            'name': 'Adina\'s Crafts'
          }
        }
      }))
    };

    // Breadcrumb schema
    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      'itemListElement': [
        {
          '@type': 'ListItem',
          'position': 1,
          'name': 'Home',
          'item': baseUrl
        },
        {
          '@type': 'ListItem',
          'position': 2,
          'name': 'Shop',
          'item': `${baseUrl}/shop`
        }
      ]
    };

    // Organization schema for brand recognition
    const organizationSchema = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      'name': 'Adina\'s Crafts',
      'url': baseUrl,
      'logo': 'https://customer-assets.emergentagent.com/job_wax-crafts/artifacts/wge2dhc1_WhatsApp%20Image%202026-01-12%20at%2012.20.17.jpeg',
      'description': 'Handcrafted scented candles and resin designs made with love in the UK.',
      'address': {
        '@type': 'PostalAddress',
        'addressCountry': 'GB'
      }
    };

    return { itemListSchema, breadcrumbSchema, organizationSchema };
  }, [products]);

  // Memoized Category Section Component
  const CategorySection = memo(({ category, categoryProducts, onAddToCart }) => {
    const Icon = category.icon;
    
    return (
      <section 
        id={category.slug} 
        className="py-12 border-b border-gray-800 last:border-b-0 scroll-mt-20"
        aria-labelledby={`${category.slug}-heading`}
      >
        <header className="flex items-center gap-4 mb-8">
          <div className="w-12 h-12 bg-amber-400/20 rounded-full flex items-center justify-center flex-shrink-0">
            <Icon className="w-6 h-6 text-amber-400" aria-hidden="true" />
          </div>
          <div>
            <h2 
              id={`${category.slug}-heading`}
              className="text-2xl md:text-3xl font-serif font-bold text-amber-100"
            >
              {category.name}
            </h2>
            <p className="text-amber-200/60">{category.description}</p>
          </div>
        </header>

        {categoryProducts.length > 0 ? (
          <div 
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
            role="list"
            aria-label={`${category.name} products`}
          >
            {categoryProducts.map((product, index) => (
              <div key={product.id} role="listitem">
                <ProductCard
                  product={product}
                  onAddToCart={onAddToCart}
                />
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 bg-gray-900/50 rounded-xl border border-gray-800">
            <Icon className="w-12 h-12 text-gray-600 mx-auto mb-4" aria-hidden="true" />
            <p className="text-gray-400">No products in this category yet.</p>
            <p className="text-gray-500 text-sm mt-1">Check back soon!</p>
          </div>
        )}
      </section>
    );
  });
  CategorySection.displayName = 'CategorySection';

  return (
    <div className="min-h-screen bg-black">
      {/* Advanced SEO with Structured Data */}
      <Helmet>
        {/* Primary Meta Tags - UK Handmade SEO Optimized */}
        <title>Shop Handmade Gifts UK | Artisan Candles & Resin Crafts</title>
        <meta name="title" content="Shop Handmade Gifts UK | Artisan Candles & Resin Crafts | Adina's Crafts" />
        <meta name="description" content="Buy handmade gifts UK. Artisan soya wax candles, elephant candles, teddy bear candles, resin clocks. Perfect for birthdays, weddings, Christmas. Free UK delivery £50+." />
        <meta name="keywords" content="handmade gifts UK, handmade home decor UK, personalised gifts UK, handmade resin crafts UK, handmade wedding gifts UK, handmade birthday gifts UK, handmade Christmas gifts UK, unique handmade presents UK, artisan handmade products UK, eco-friendly handmade gifts UK, handmade craft shop UK, handmade gift ideas UK, custom handmade gifts UK, soya wax candles UK, elephant candle UK, teddy bear candle UK, flower candles UK, angel wing candles UK, resin clock UK, handmade candles Aylesbury, handmade gifts Milton Keynes, artisan crafts Buckinghamshire, handmade Oxford, craft shop High Wycombe, handmade gifts Luton, handmade Hemel Hempstead, unique gifts Tring" />
        <link rel="canonical" href="https://www.adinascrafts.co.uk/shop" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.adinascrafts.co.uk/shop" />
        <meta property="og:title" content="Shop Handmade Gifts UK | Artisan Candles & Resin Crafts" />
        <meta property="og:description" content="Buy handmade gifts UK. Artisan soya wax candles, resin art, personalised gifts for birthdays, weddings, Christmas. Free UK delivery over £50." />
        <meta property="og:image" content="https://res.cloudinary.com/dsez5ypdi/image/upload/w_1200,h_630,c_fill,g_auto,q_auto,f_auto/gallery/gallery_2_colorful_candles.jpg" />
        <meta property="og:image:alt" content="Handmade artisan candles and gifts UK - Adina's Crafts shop" />
        <meta property="og:site_name" content="Adina's Crafts - Handmade Gifts UK" />
        <meta property="og:locale" content="en_GB" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://www.adinascrafts.co.uk/shop" />
        <meta name="twitter:title" content="Shop Handmade Gifts UK | Adina's Crafts" />
        <meta name="twitter:description" content="Buy unique handmade gifts UK. Artisan candles, resin art & personalised gifts. Free UK delivery £50+." />
        <meta name="twitter:image" content="https://res.cloudinary.com/dsez5ypdi/image/upload/w_1200,h_630,c_fill,g_auto,q_auto,f_auto/gallery/gallery_2_colorful_candles.jpg" />
        <meta name="twitter:image:alt" content="Handmade artisan gifts UK - Adina's Crafts" />
        
        {/* Additional SEO */}
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="author" content="Adina's Crafts" />
        <meta name="geo.region" content="GB-BKM" />
        <meta name="geo.placename" content="Buckinghamshire, United Kingdom" />
        
        {/* Structured Data - ItemList */}
        <script type="application/ld+json">
          {JSON.stringify(generateStructuredData.itemListSchema)}
        </script>
        
        {/* Structured Data - Breadcrumbs */}
        <script type="application/ld+json">
          {JSON.stringify(generateStructuredData.breadcrumbSchema)}
        </script>
        
        {/* Structured Data - Organization */}
        <script type="application/ld+json">
          {JSON.stringify(generateStructuredData.organizationSchema)}
        </script>
      </Helmet>
      
      <Navbar cartCount={cartCount} />

      {/* Hero Section with semantic HTML */}
      <header className="py-16 text-white text-center bg-gradient-to-b from-black to-gray-900 border-b border-gray-800">
        <div className="container mx-auto px-6">
          <h1 className="text-4xl md:text-5xl font-serif font-bold mb-4 text-amber-100">
            Our Collection
          </h1>
          <p className="text-lg text-amber-200/70 max-w-2xl mx-auto mb-8">
            Discover our handcrafted creations made with love and care
          </p>

          {/* Category Quick Links - Navigation */}
          <nav aria-label="Product categories">
            <ul className="flex flex-wrap justify-center gap-4">
              {CATEGORIES.map(cat => {
                const Icon = cat.icon;
                return (
                  <li key={cat.id}>
                    <a
                      href={`#${cat.slug}`}
                      className="flex items-center gap-2 px-6 py-3 bg-gray-800/50 hover:bg-amber-400/20 rounded-full transition-colors border border-gray-700 hover:border-amber-400/50"
                      title={cat.seoDescription}
                    >
                      <Icon className="w-5 h-5 text-amber-400" aria-hidden="true" />
                      <span className="text-white">{cat.name}</span>
                      <span 
                        className="text-xs bg-amber-400/20 text-amber-300 px-2 py-0.5 rounded-full"
                        aria-label={`${categoryCounts[cat.id]} products`}
                      >
                        {categoryCounts[cat.id]}
                      </span>
                    </a>
                  </li>
                );
              })}
            </ul>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main>
        {/* Error State with Retry */}
        {loadError && !isLoading ? (
          <div className="container mx-auto px-6 py-20 text-center">
            <div className="max-w-md mx-auto bg-gray-900/50 rounded-xl p-8 border border-gray-800">
              <svg className="w-16 h-16 text-amber-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <h2 className="text-xl font-serif font-bold text-amber-100 mb-2">Unable to Load Products</h2>
              <p className="text-amber-200/70 mb-6">There was a problem connecting to our server. Please try again.</p>
              <button
                onClick={() => {
                  setIsLoading(true);
                  loadProducts();
                }}
                className="inline-flex items-center gap-2 px-6 py-3 bg-amber-400 hover:bg-amber-500 text-black font-semibold rounded-full transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Retry
              </button>
            </div>
          </div>
        ) : isLoading ? (
          /* Loading State with Skeleton */
          <div className="container mx-auto px-6 py-8">
            {CATEGORIES.map(category => (
              <section key={category.id} className="py-12 border-b border-gray-800 last:border-b-0">
                <div className="flex items-center gap-4 mb-8">
                  <div className="w-12 h-12 bg-gray-700/50 rounded-full animate-pulse" />
                  <div className="space-y-2">
                    <div className="h-7 bg-gray-700/50 rounded w-48 animate-pulse" />
                    <div className="h-4 bg-gray-700/50 rounded w-64 animate-pulse" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {[1, 2, 3, 4].map(i => (
                    <ProductSkeleton key={i} />
                  ))}
                </div>
              </section>
            ))}
          </div>
        ) : (
          /* Category Sections */
          <div className="container mx-auto px-6 py-8">
            {CATEGORIES.map(category => (
              <CategorySection 
                key={category.id} 
                category={category}
                categoryProducts={getProductsByCategory(category.id)}
                onAddToCart={handleAddToCart}
              />
            ))}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default Shop;
